<script setup>
import ga4 from '#common/utils/ga4'
import { getClubCrumbs } from '#common/utils/wineclub'

const { $auth } = useNuxtApp()
const route = useRoute()
const config = useRuntimeConfig()
const metadata = useMetadata()
const { data: podcastData } = await useFetch('/api/podcast/main/')
const { data: membershipData } = await useFetch(
  '/api/membership/unfiltered-club/',
)
const PODCAST_JOIN_REDIRECT = '/podcast/join/'
const isFull = computed(() =>
  membershipData.value?.plans.every((p) => p.is_full),
)
const deliveryFrequency = computed(
  () => membershipData.value?.delivery_freq_info,
)
const pricing = computed(() => membershipData.value?.pricing_info)
const hasActiveGiftTerm = computed(
  () => membershipData.value?.has_active_gift_term,
)
const breadcrumbs = computed(() =>
  getClubCrumbs('Unfiltered Wine Podcast & Wine Club'),
)
const ctaText = computed(() => {
  if (isFull.value) {
    return 'Join The Waiting List'
  } else {
    return 'Join the Club'
  }
})
const distinctSeasons = computed(() => {
  const ds = []
  for (const episode of podcastData.value?.all_episodes || []) {
    const season = Number(episode.season)
    if (!ds.includes(season)) {
      ds.push(season)
    }
  }
  ds.sort().reverse()
  return ds
})

function openLoginModal() {
  $auth.openAuthModal({
    redirect: PODCAST_JOIN_REDIRECT,
    isClub: true,
  })
}
function signup() {
  if (!$auth.loggedIn) {
    openLoginModal()
  } else {
    navigateTo(PODCAST_JOIN_REDIRECT)
  }
}
function seasonEpisodes(season) {
  return (
    podcastData.value?.all_episodes.filter((episode) => {
      return Number(episode.season) === season
    }) || []
  )
}
function goToLinktree(episode) {
  window.location.href = episode.destination_url
}

const seoOverrides = metadata.seoForPath(route.fullPath)
useHead(
  !seoOverrides || !seoOverrides.og_image_url
    ? {
        meta: [
          {
            hid: 'og:image',
            name: 'og:image',
            property: 'og:image',
            content: `${config.public.cloudfrontBase}images/podcast.png`,
          },
          {
            hid: 'og:image:width',
            name: 'og:image:width',
            property: 'og:image:width',
            content: '1280',
          },
          {
            hid: 'og:image:height',
            name: 'og:image:height',
            property: 'og:image:height',
            content: '720',
          },
          {
            hid: 'og:image:type',
            name: 'og:image:type',
            property: 'og:image:type',
            content: 'image/png',
          },
        ],
      }
    : {},
)
onMounted(() => {
  ga4.trackViewClub({ slug: 'podcast' })
})
</script>

<template>
  <div class="podcasts">
    <section class="hero">
      <header>
        <img
          src="~/assets/images/podcasts-logo.svg"
          alt="Wine Access Unfiltered Logo"
          role="img"
        />
        <hr />
        <h1>
          <span>Wine Access Unfiltered:</span>
          Official Podcast and Wine Club
        </h1>
      </header>
    </section>
    <Breadcrumb :breadcrumbs="breadcrumbs" />
    <div class="podcasts-content">
      <section class="feature">
        <div class="copy-container">
          <header>
            <p class="p-heading">
              Where Fine-tuned Palates Meet Fresh Perspectives
            </p>
          </header>
          <div class="copy">
            <p>
              Wine Access Unfiltered goes deep into what we really love about
              wine: the intimate moments, unforgettable occasions, and hilarious
              stories that a great bottle brings about.
            </p>
            <p>
              Hosted by former PRESS wine director and Napa Valley insider
              Amanda McCrossin with appearances from our team of unrivaled
              experts, we’re popping our favorite bottles and sharing them with
              you.
            </p>
            <p>
              The Wine Access Unfiltered Club delivers a completely new wine
              education experience. Once every two months, you’ll receive four
              bottles. They will all be featured and discussed on the Wine
              Access Unfiltered podcast. That way, you can drink along with us
              as we tell stories, share knowledge, and answer your questions!
            </p>
            <p></p>
          </div>
        </div>
      </section>
      <section class="primary-value">
        <div>
          <header>
            <p class="p-heading">{{ pricing }}</p>
            <h3>{{ deliveryFrequency }}</h3>
          </header>
          <div class="button-link">
            <p>
              <a class="button primary" @click="signup()">
                <span class="button-inset">
                  {{ ctaText }}
                </span>
              </a>
            </p>
            <p v-if="hasActiveGiftTerm">
              <span class="gift-alternative">
                <nuxt-link
                  to="/wine-club/gifting/unfiltered-club/"
                  class="button"
                >
                  Or gift it
                </nuxt-link>
              </span>
            </p>
          </div>
          <p>And enjoy 10% off all Wine Access purchases *</p>
        </div>
      </section>
      <section class="listen callout site-width">
        <div class="cta-callout">
          <div class="embed-container">
            <div class="embed">
              <iframe
                :src="podcastData?.trailer_url"
                width="640"
                height="360"
                frameborder="0"
                allowfullscreen=""
              ></iframe>
            </div>
          </div>
          <h2>Listen to Our Podcast</h2>
          <ul class="generic-grid">
            <li v-if="podcastData?.apple_podcasts_url">
              <a :href="podcastData?.apple_podcasts_url">
                <img
                  src="~/assets/images/podcasts-apple.svg"
                  alt="Apple Podcasts Logo"
                  role="presentation"
                />
                Apple Podcasts
              </a>
            </li>
            <li v-if="podcastData?.spotify_url">
              <a :href="podcastData?.spotify_url">
                <img
                  src="~/assets/images/podcasts-spotify.svg"
                  alt="Spotify Logo"
                  role="presentation"
                />
                Spotify
              </a>
            </li>
            <li v-if="podcastData?.google_play_music_url">
              <a :href="podcastData?.google_play_music_url">
                <img
                  src="~/assets/images/podcasts-google.svg"
                  alt="Google Play Music Logo"
                  role="presentation"
                />
                Google Play Music
              </a>
            </li>
          </ul>
        </div>
      </section>
      <section class="shaded recent-podcasts">
        <div v-for="season in distinctSeasons" :key="season">
          <header>
            <h3>Unfiltered: Season {{ season }}</h3>
          </header>
          <ul class="generic-grid">
            <li
              v-for="episode in seasonEpisodes(season)"
              :key="episode.id"
              @click="goToLinktree(episode)"
            >
              <span class="image"
                ><img :src="episode.image_url" role="presentation" alt=""
              /></span>
              <span class="details">
                <span class="episode"
                  >Episode {{ episode.episode_number }}</span
                >
                <span class="date">{{
                  $moment(episode.date).format('MMMM D, YYYY')
                }}</span>
              </span>
              <span class="title" v-html="episode.description"></span>
            </li>
          </ul>
          <hr v-if="season != distinctSeasons.at(-1)" />
        </div>
      </section>
    </div>
  </div>
</template>
